import { isAfter } from 'date-fns/isAfter';
import React, { JSX, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useDebouncedCallback } from 'use-debounce';
import { Routes } from '../../../app/Routes';
import {
  BlechconCustomerConfigurationFrontend,
  BlechconOrder,
  BlechconOrderDelivery,
  BlechconUserInfo,
  clearOrderCalculations,
  CustomerReference,
} from '../../../blechcon';
import { Button } from '../../../components/Button/Button';
import { Checkbox } from '../../../components/Checkbox/Checkbox';
import { DateInput } from '../../../components/DateInput/DateInput';
import { FileTableRowComponent } from '../../../components/FileTableRowComponent/FileTableRowComponent';
import { GrindingWarningsInfo } from '../../../components/GrindingWarningsInfo/GrindingWarningsInfo';
import { Input } from '../../../components/Input/Input';
import { OrderPrintButton } from '../../../components/OrderPrintButton';
import { OrderTotal } from '../../../components/OrderTotal/OrderTotal';
import { persistAnalysisModels } from '../../../components/PartViewer/persistAnalysisModels';
import { localized } from '../../../config/localization';
import { OrderAddress } from './OrderAddress';
import './OrderPage.css';
import { skipSubmitCheck } from '../Checkout';

type PageProps = {
  readonly order: BlechconOrder;
  readonly userInfo: BlechconUserInfo;
  readonly isAdmin: boolean;
  readonly configuration: BlechconCustomerConfigurationFrontend;
  readonly persistPending: boolean;
  readonly persistOrder: (order: Partial<BlechconOrder>) => Promise<BlechconOrder | null>;
  readonly finalizeOrder: (order: Partial<BlechconOrder>) => Promise<BlechconOrder | null>;
};

export function OrderPage({
  order,
  userInfo,
  persistPending,
  isAdmin,
  persistOrder,
  finalizeOrder,
  configuration,
}: PageProps): JSX.Element | null {
  const navigate = useNavigate();
  const [desiredDate, setDesiredDate] = useState<string | undefined>(order.delivery?.desiredDate);
  const [customerReference, setCustomerReference] = useState<CustomerReference | undefined>(order.customerReference);
  const [areTermsAndConditionsAccepted, setAreTermsAndConditionsAccepted] = useState<boolean>(false);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);

  useEffect(() => {
    if (order.delivery) {
      setDesiredDate(order.delivery.desiredDate);
    }
  }, [order.delivery]);

  const persistOrderDebounced = useDebouncedCallback(async (partial: Partial<BlechconOrder>) => {
    try {
      if (!isConfirming) {
        await persistOrder({ id: order.id, ...partial });
      }
    } catch (error) {
      toast.error(`${localized.GENERAL_ERROR}: ${String(error)}`);
    }
  }, 2000);

  const confirmOrder = useCallback(async () => {
    setIsConfirming(true);
    try {
      const updatedOrder = await finalizeOrder({
        id: order.id,
        customerReference,
        delivery: { ...order.delivery, desiredDate },
      });

      if (updatedOrder) {
        skipSubmitCheck();
        navigate(Routes.checkoutSuccess(order.id));
      }
    } catch (error) {
      setIsConfirming(false);
      toast.error(`${localized.GENERAL_ERROR}: ${String(error)}`);
    }
  }, [customerReference, desiredDate, finalizeOrder, navigate, order.delivery, order.id]);

  const minimumOrderValueInCent =
    configuration.customerMinimumOrderValueInCent || configuration.MINIMUM_ORDER_VALUE_IN_CENT;
  const minMaterialTotalPriceReached = (order.prices.materialTotalPrice ?? 0) > minimumOrderValueInCent / 100;
  const calculationExpired = isAfter(new Date(), new Date(order.prices.expiresAtDate ?? 0));

  const isConfirmEnabled =
    areTermsAndConditionsAccepted &&
    (order.prices.totalPrice ?? 0) > 0 &&
    !calculationExpired &&
    minMaterialTotalPriceReached &&
    !persistPending &&
    order.delivery?.invoiceAddress &&
    (order.delivery.selfPickup || order.delivery.shippingAddress);

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <td className="table-cell__product">{localized.PRODUCT}</td>
            <td>{localized.QUANTITY}</td>
            <td>{localized.UNIT_PRICE} EUR</td>
            <td className="table-cell--right">{localized.TOTAL} EUR</td>
          </tr>
        </thead>
        <tbody className="no-hover">
          {order.files.map((file, index) => {
            return (
              <FileTableRowComponent
                key={file.id}
                file={file}
                index={index}
                disabled={persistPending}
                adminOnly={isAdmin}
                onAnalysisResult={async (analysisModels) => {
                  await persistAnalysisModels(order, file, analysisModels, persistOrder, !isConfirming);
                }}
                configuration={configuration}
              />
            );
          })}
        </tbody>
      </table>

      <GrindingWarningsInfo files={order.files} />

      <OrderAddress
        userInfo={userInfo}
        delivery={order.delivery}
        disabled={persistPending}
        onChange={(delivery: BlechconOrderDelivery) => persistOrderDebounced({ id: order.id, delivery })}
      />

      <div className="order-page__delivery_dates">
        <DateInput
          name="delivery_date_desired"
          label={order.delivery?.selfPickup ? localized.PICKUP_DATE_DESIRED : localized.DELIVERY_DATE_DESIRED}
          placeholder={order.delivery?.selfPickup ? localized.PICKUP_DATE_DESIRED : localized.DELIVERY_DATE_DESIRED}
          value={desiredDate ? new Date(desiredDate) : undefined}
          minDays={configuration.DESIRED_DATE_MIN_DAYS}
          maxDays={configuration.DESIRED_DATE_MAX_DAYS}
          workDaysOnly={true}
          disabled={persistPending}
          onChange={(value) => {
            setDesiredDate(value?.toISOString());
            persistOrderDebounced({
              delivery: {
                ...order.delivery,
                desiredDate: value?.toISOString(),
              },
            });
          }}
        />
        <DateInput
          name="delivery_date_estimated"
          label={order.delivery?.selfPickup ? localized.PICKUP_DATE_ESTIMATED : localized.DELIVERY_DATE_ESTIMATED}
          readonly={true}
          value={order.delivery?.deliveryDate ? new Date(order.delivery.deliveryDate) : undefined}
        />
      </div>

      <div>
        <div className="order-page__customer-reference">{localized.CUSTOMER_REFERENCE}</div>
        <div className="order-page__customer-reference-input">
          <Input
            name="customerReference"
            type="text"
            placeholder={localized.CUSTOMER_REFERENCE}
            values={{ customerReference: customerReference ?? '' }}
            disabled={persistPending}
            onChange={(event) => {
              const value = event.target.value;
              setCustomerReference(value);
              persistOrderDebounced({ customerReference: value });
            }}
          />
        </div>
        <div className="order-page__terms">
          <Checkbox
            checked={areTermsAndConditionsAccepted}
            onClick={() => setAreTermsAndConditionsAccepted(!areTermsAndConditionsAccepted)}
            dataCy="acceptTerms"
            label={localized.formatString<JSX.Element>(
              localized.ACCEPT_TERMS,
              <a
                href="https://blechcon.de/de/agb.html"
                target="_blank"
                onClick={(event) => event.stopPropagation()}
                rel="noopener noreferrer"
                className="order-page__terms-link"
              >
                {localized.TERMS_AND_CONDITIONS}
              </a>
            )}
          />
        </div>

        <OrderTotal
          totalPrice={order.prices.totalPrice ?? 0}
          materialTotalPrice={order.prices.materialTotalPrice ?? 0}
          shippingCost={order.prices.shippingCost}
          selfPickup={order.delivery?.selfPickup ?? false}
          discountPrice={order.prices.discountPrice ?? 0}
        />
      </div>

      <div className="order_page__actions">
        <Button
          disabled={false}
          onClick={async () => {
            await persistOrder({ ...clearOrderCalculations(order), checkoutStep: 3 });
            navigate(Routes.checkoutCalculation(order.id));
          }}
          secondary
        >
          {localized.BACK_TO_CALCULATION}
        </Button>
        {calculationExpired ? <span className="order-page__price-expired">{localized.PRICE_EXPIRED}</span> : null}
        {!minMaterialTotalPriceReached ? (
          <span className="order-page__min-value">
            {localized.formatString(localized.MIN_VALUE_NOT_REACHED, minimumOrderValueInCent / 100 + '€')}
          </span>
        ) : null}

        <div>
          <OrderPrintButton disabled={!(isConfirmEnabled && order.customerId === userInfo.customerId)} order={order} />

          <Button
            disabled={!(isConfirmEnabled && order.userId === userInfo.userId)}
            onClick={confirmOrder}
            dataCy="confirm-order-button"
          >
            {localized.CONFIRM_ORDER}
          </Button>
        </div>
      </div>
    </>
  );
}
